import { RoomRule, RoomRuleData } from 'types/Room';

export interface ParsedTotalCountRule {
    quantity: number;
}

export interface ParsedTotalWeightRule {
    totalWeight: number;
}

export interface ParsedWeightRangeRule {
    quantity: number;
    weightFrom: number | null;
    weightTo: number | null;
}

export interface ParsedPetSizeEntry {
    petTypeId: string;
    sizeId: string;
    quantity: number;
}

export const parseJSON = <T>(value: unknown, fallback: T): T | unknown => {
    if (typeof value === 'string') {
        try {
            return JSON.parse(value);
        } catch {
            return fallback;
        }
    }
    return value;
};

export const getNumericValue = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string' && value.trim().length > 0) {
        const parsedValue = Number(value);

        if (Number.isFinite(parsedValue)) {
            return parsedValue;
        }
    }

    return null;
};

export const getRecordValue = (value: unknown): Record<string, unknown> | null => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        return value as Record<string, unknown>;
    }
    return null;
};

export const getFirstNumericValue = (
    record: Record<string, unknown>,
    keys: Array<string>
): number | null => {
    for (const key of keys) {
        const value = getNumericValue(record[key]);

        if (value !== null) {
            return value;
        }
    }

    return null;
};

export const parseTotalCountRule = (
    ruleData: RoomRuleData | string
): ParsedTotalCountRule | null => {
    const data = parseJSON(ruleData, null);
    const record = getRecordValue(data);

    if (!record) {
        return null;
    }

    const quantity = getFirstNumericValue(record, ['quantity']);

    if (quantity === null) {
        return null;
    }

    return { quantity };
};

export const parseTotalWeightRule = (
    ruleData: RoomRuleData | string
): ParsedTotalWeightRule | null => {
    const data = parseJSON(ruleData, null);
    const record = getRecordValue(data);

    if (!record) {
        return null;
    }

    // Handle legacy "0" key structure: {"0":{"uuid":"...","totalWeight":null},"totalWeight":120}
    const totalWeight = getFirstNumericValue(record, [
        'totalWeight',
        'total_pet_weight',
        'maxWeight',
        'max_weight',
        'weight',
        'value'
    ]);

    if (totalWeight === null) {
        return null;
    }

    return { totalWeight };
};

export const parseWeightRangeRule = (
    ruleData: RoomRuleData | string
): ParsedWeightRangeRule | null => {
    const data = parseJSON(ruleData, null);

    // WeightRange can be either object or array format
    // Object: {"quantity":4,"weightFrom":15,"weightTo":60}
    // Array: [{"quantity":4,"weightFrom":15,"weightTo":60}]
    let record: Record<string, unknown> | null = null;

    if (Array.isArray(data)) {
        const firstEntry = data[0];
        record = getRecordValue(firstEntry);
    } else {
        record = getRecordValue(data);
    }

    if (!record) {
        return null;
    }

    const quantity = getFirstNumericValue(record, ['quantity']) ?? 1;

    const weightFrom = getFirstNumericValue(record, [
        'weightFrom',
        'weight_from',
        'fromWeight',
        'from_weight',
        'minWeight',
        'min_weight',
        'min'
    ]);

    const weightTo = getFirstNumericValue(record, [
        'weightTo',
        'weight_to',
        'toWeight',
        'to_weight',
        'maxWeight',
        'max_weight',
        'max'
    ]);

    // Must have at least one weight boundary
    if (weightFrom === null && weightTo === null) {
        return null;
    }

    return {
        quantity,
        weightFrom,
        weightTo
    };
};

export const parsePetSizeRules = (ruleData: RoomRuleData | string): Array<ParsedPetSizeEntry> => {
    const data = parseJSON(ruleData, []);

    if (!Array.isArray(data)) {
        return [];
    }

    return data
        .map(entry => {
            const record = getRecordValue(entry);

            if (!record) {
                return null;
            }

            const petTypeId = record.petTypeId ?? record.pet_type_id;
            const sizeId = record.sizeId ?? record.size_id;
            const quantity = getNumericValue(record.quantity);

            if (typeof petTypeId !== 'string' || typeof sizeId !== 'string' || quantity === null) {
                return null;
            }

            return {
                petTypeId,
                sizeId,
                quantity
            };
        })
        .filter((entry): entry is ParsedPetSizeEntry => entry !== null);
};

export const parseRuleDataByType = (
    rule: RoomRule
): ParsedTotalCountRule | ParsedTotalWeightRule | ParsedWeightRangeRule | null => {
    const { ruleTypeId, ruleData, rawRuleData } = rule;
    const source = rawRuleData || ruleData;

    switch (ruleTypeId) {
        case 1: // TotalCount
            return parseTotalCountRule(source);
        case 3: // TotalPetWeight
            return parseTotalWeightRule(source);
        case 4: // WeightRange
            return parseWeightRangeRule(source);
        default:
            return null;
    }
};

export const getPetSizeEntriesFromRule = (rule: RoomRule): Array<ParsedPetSizeEntry> => {
    if (rule.ruleTypeId !== 2) {
        return [];
    }

    return parsePetSizeRules(rule.rawRuleData || rule.ruleData);
};
